import { useMemo, useState } from "react";
import { MyAccountStoreProduct, MyAccountStoreFilterDimension } from "../types";

type SelectedByDimension = Record<string, Set<string>>;

interface Output {
  selectedByDimension: SelectedByDimension;
  toggleValue: (dimensionKey: string, value: string) => void;
  clearAll: () => void;
  filteredProducts: MyAccountStoreProduct[];
  hasActiveFilters: boolean;
  /** Per-dimension list of selectable values, falling back to whatever the
   *  loaded products carry when the config doesn't ship a master list. */
  valuesByDimension: Record<string, string[]>;
  /** Per-dimension, per-value product count (how many products carry that
   *  value), shown as "(N)" next to each option — like the CMS category page.
   *  Counted over the full product list so the numbers stay stable while
   *  filtering. */
  countsByDimension: Record<string, Record<string, number>>;
}

interface Input {
  products: MyAccountStoreProduct[];
  dimensions: MyAccountStoreFilterDimension[];
}

/**
 * Manages filter selection state and applies the predicate.
 *
 * Within a dimension: OR (any selected value matches).
 * Across dimensions:   AND (all active dimensions must match).
 */
export const useMyAccountStoreFilters = (input: Input): Output => {
  const { products, dimensions } = input;

  const [selectedByDimension, setSelectedByDimension] = useState<SelectedByDimension>(
    () => Object.fromEntries(dimensions.map((d) => [d.dimensionKey, new Set<string>()])),
  );

  const toggleValue = (dimensionKey: string, value: string): void => {
    setSelectedByDimension((prev) => {
      const next: SelectedByDimension = { ...prev };
      const set = new Set(prev[dimensionKey] ?? []);
      if (set.has(value)) set.delete(value);
      else set.add(value);
      next[dimensionKey] = set;
      return next;
    });
  };

  const clearAll = (): void => {
    setSelectedByDimension(
      Object.fromEntries(dimensions.map((d) => [d.dimensionKey, new Set<string>()])),
    );
  };

  const hasActiveFilters = useMemo(
    () => Object.values(selectedByDimension).some((s) => s.size > 0),
    [selectedByDimension],
  );

  const filteredProducts = useMemo(() => {
    if (!hasActiveFilters) return products;
    return products.filter((product) => {
      for (const dimensionKey of Object.keys(selectedByDimension)) {
        const selected = selectedByDimension[dimensionKey];
        if (!selected || selected.size === 0) continue;
        const productValues = product.dimensions.find((d: { dimensionKey: string; values: string[] }) => d.dimensionKey === dimensionKey)?.values ?? [];
        const matches = productValues.some((v: string) => selected.has(v));
        if (!matches) return false;
      }
      return true;
    });
  }, [products, selectedByDimension, hasActiveFilters]);

  const valuesByDimension = useMemo<Record<string, string[]>>(() => {
    const result: Record<string, string[]> = {};
    for (const dimension of dimensions) {
      if (dimension.values && dimension.values.length > 0) {
        result[dimension.dimensionKey] = dimension.values;
        continue;
      }
      const set = new Set<string>();
      for (const product of products) {
        const productValues = product.dimensions.find((d: { dimensionKey: string; values: string[] }) => d.dimensionKey === dimension.dimensionKey)?.values ?? [];
        productValues.forEach((v: string) => set.add(v));
      }
      result[dimension.dimensionKey] = Array.from(set).sort((a, b) => a.localeCompare(b));
    }
    return result;
  }, [dimensions, products]);

  const countsByDimension = useMemo<Record<string, Record<string, number>>>(() => {
    const result: Record<string, Record<string, number>> = {};
    for (const dimension of dimensions) {
      const counts: Record<string, number> = {};
      for (const product of products) {
        const productValues =
          product.dimensions.find(
            (d: { dimensionKey: string; values: string[] }) => d.dimensionKey === dimension.dimensionKey,
          )?.values ?? [];
        productValues.forEach((v: string) => {
          counts[v] = (counts[v] ?? 0) + 1;
        });
      }
      result[dimension.dimensionKey] = counts;
    }
    return result;
  }, [dimensions, products]);

  return {
    selectedByDimension,
    toggleValue,
    clearAll,
    filteredProducts,
    hasActiveFilters,
    valuesByDimension,
    countsByDimension,
  };
};
